import React from "react";
import { Link } from "react-router-dom";
import { TERMS_TEXT, PRIVACY_TEXT } from "./legalContent";
import LegalLinks from "./LegalLinks";

// Renders the small markdown subset used in legalContent.js (headings, lists, tables, bold, links).
const inline = (text, key) => {
  const parts = text.split(/(\*\*[^*]+\*\*|https?:\/\/[^\s)]+)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={`${key}-${i}`}>{part.slice(2, -2)}</strong>;
    if (/^https?:\/\//.test(part))
      return <a key={`${key}-${i}`} href={part} target="_blank" rel="noopener noreferrer" className="text-[#0099ff] underline break-all">{part}</a>;
    return part;
  });
};

const render = (md) => {
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim() || line.trim() === "---") { if (line.trim() === "---") out.push(<hr key={i} className="my-6 border-gray-200" />); i += 1; continue; }
    if (line.startsWith("# ")) { out.push(<h1 key={i} className="text-2xl md:text-3xl font-bold text-[#292929] mb-4">{line.slice(2)}</h1>); i += 1; continue; }
    if (line.startsWith("## ")) { out.push(<h2 key={i} className="text-lg md:text-xl font-bold text-[#292929] mt-8 mb-3">{line.slice(3)}</h2>); i += 1; continue; }
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) { items.push(lines[i].slice(2)); i += 1; }
      out.push(<ul key={i} className="list-disc pl-6 space-y-1 mb-4">{items.map((t, j) => <li key={j}>{inline(t, `${i}-${j}`)}</li>)}</ul>);
      continue;
    }
    if (line.startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) { rows.push(lines[i]); i += 1; }
      const cells = (r) => r.split("|").slice(1, -1).map((c) => c.trim());
      const [head, , ...body] = rows;
      out.push(
        <div key={i} className="overflow-x-auto mb-4">
          <table className="w-full text-sm border border-gray-200">
            <thead className="bg-gray-50"><tr>{cells(head).map((c, j) => <th key={j} className="text-left p-2 border-b border-gray-200 font-semibold">{c}</th>)}</tr></thead>
            <tbody>{body.map((r, k) => <tr key={k}>{cells(r).map((c, j) => <td key={j} className="p-2 border-b border-gray-100 align-top">{inline(c, `${i}-${k}-${j}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }
    // Paragraph: consecutive non-empty lines joined with line breaks.
    const para = [];
    while (i < lines.length && lines[i].trim() && !/^(#|- |\||---)/.test(lines[i])) { para.push(lines[i]); i += 1; }
    out.push(<p key={i} className="mb-4 leading-relaxed">{para.map((p, j) => <React.Fragment key={j}>{j > 0 && <br />}{inline(p, `${i}-${j}`)}</React.Fragment>)}</p>);
  }
  return out;
};

// Public page (no sign-in): /terms or /privacy.
const LegalPage = ({ doc }) => {
  const text = doc === "privacy" ? PRIVACY_TEXT : TERMS_TEXT;
  return (
    <div className="h-screen overflow-y-auto bg-white">
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12 text-[#3d3d3d] text-[15px]">
        <Link to="/" className="text-[#0099ff] text-sm font-semibold">← Morphosys</Link>
        <article className="mt-6">{render(text)}</article>
        <LegalLinks className="mt-10 pt-6 border-t border-gray-200" />
      </div>
    </div>
  );
};

export default LegalPage;
